<template>
    <section class="title">
        {{ $t('feedback.title') }}
    </section>

    <section class="main_type">
        <FeedbackQuestionTree
            :target-select="form.question"
            :question-list="feedbackQuestionList"
            :on-question-update="onQuestionSelect"
        />
    </section>

    <section class="subject">
        <input
            v-model="form.subject"
            name="subject"
            :placeholder="$t('feedback.subject')"
            required="required"
            type="text"
        >
    </section>

    <textarea
        v-model="form.description"
        name="description"
        :placeholder="$t('feedback.description')"
        required="required"
    />

    <section class="email">
        <input
            v-model="form.email"
            name="email"
            :placeholder="$t('feedback.email')"
            required="required"
            type="email"
            autocomplete="email"
        >
    </section>

    <section>
        <div
            class="attachment"
            @drop.prevent="onDropFiles"
            @dragover.prevent
        >
            <div class="attachment-text">
                <div class="title">
                    {{ $t('feedback.attachment1') }}
                </div>
                <div class="size">
                    {{ $t('feedback.attachment2', { max_file_size_in_mb: maxFileSize }) }}
                </div>
            </div>
            <input
                ref="fileInput"
                type="file"
                name="attachment"
                multiple="multiple"
                accept="image/*, video/*, text/plain"
                :disabled="isUploadDisabled"
                @click="preventDoubleClickToUpload"
                @change="onInputFiles"
            >
        </div>
    </section>

    <section class="files">
        <ul class="files__wrapper">
            <li
                v-for="attachment in form.attachments"
                :key="attachment.token"
                class="files__item"
            >
                <span class="files__item-label">{{ attachment.name }}</span>
                <button
                    class="files__button"
                    @click.prevent.stop="removeAttachment(attachment.token)"
                >
                    <span class="files__close-icon" />
                </button>
            </li>
        </ul>
    </section>

    <section class="send">
        <button
            type="button"
            class="feedback-form__button"
            @click.prevent.stop="sendTicket"
        >
            {{ $t('feedback.send') }}
        </button>
    </section>
</template>

<script>
import messagePostProcess from '@/modules/messagePostProcessor';
import { mapState, mapMutations, mapGetters } from 'vuex';
import { MESSAGE_TYPE } from '@/constants/message';
import { EMAIL_REG_EX } from '@/constants/emailRegEx';
import api from '@/api';
import FeedbackQuestionTree from '@/components/FeedbackQuestionTree';

const UPLOAD_DELAY_MS = 1000;
const EMAIL_FIELD_MAX_LENGTH = 255;

export default {
    name: 'FeedbackForm',
    components: {
        FeedbackQuestionTree
    },
    data: () => ({
        form: {
            subject: '',
            description: '',
            email: '',
            attachments: [],
            question: {}
        },
        waitTimeout: 0,

        isUploadDisabled: false,
        isUploadErrorAlreadyWas: false
    }),
    computed: {
        ...mapState({
            queryParams: state => state.queryParams,
            preset: state => state.feedback.preset,
            networkTestData: state => state.feedback.networkTest.data,
            feedbackQuestionList: state => state.feedback.feedbackQuestionList.data,
            cachedFormData: state => state.feedback.cachedFormData,
        }),
        ...mapGetters({
            maxFileSize: 'appConfig/getMaxFileSize',
            maxFilesCount: 'appConfig/getMaxFilesCount',
            descriptionMinLength: 'appConfig/getDescriptionMinLength',
            sendTimeout: 'appConfig/getSendTimeout'
        }),
        questionId() {
            return this.form.question.id;
        }
    },
    mounted() {
        if (this.preset) this.setPreset()
        this.setFormDataFromCache()
    },
    beforeUnmount() {
        if (this.checkFormWasChanged()) {
            this.setCachedFormData(this.form)
        }
    },
    methods: {
        ...mapMutations({
            showMessage: 'message/show',
            hideMessage: 'message/hide',
            setCachedFormData: 'feedback/setCachedFormData',
        }),
        checkFormWasChanged() {
            return this.form.subject !== ''
                || this.form.description !== ''
                || this.form.email !== ''
                || this.form.attachments.length
                || Object.keys(this.form.question).length
        },
        setFormDataFromCache() {
            if (Object.keys(this.cachedFormData).length) {
                this.form = this.cachedFormData
            }
        },
        onQuestionSelect(question) {
            if (question.attributes.alert) {
                const alertText = messagePostProcess(question.attributes.alert)
                this.showMessage({
                    text: alertText,
                })
            }
            this.form.question = question
        },
        setPreset() {
            const isPresetFeedbackQuestion = q => q.id === this.preset.relationships.feedback_questions.data.id
            const findFeedbackQuestion = (fql) => {
                for (let fq of fql) {
                    if (isPresetFeedbackQuestion(fq)) return fq
                    if (fq.children?.length) {
                        const result = findFeedbackQuestion(fq.children)
                        if (result) return result
                    }
                }
                return false
            }

            const result = findFeedbackQuestion(this.feedbackQuestionList)

            if (!result) throw 'preset :: feedback question not found'

            this.form.question = result
            this.form.subject = this.preset.attributes.subject
            this.form.description = this.preset.attributes.description
        },
        preventDoubleClickToUpload() {
            setTimeout(() => {
                this.isUploadDisabled = true;
            }, 0);
            setTimeout(() => {
                this.isUploadDisabled = false;
            }, UPLOAD_DELAY_MS);
        },
        onDropFiles(event) {
            const files = event.dataTransfer.files;
            this.saveAttachments(files);
            this.cleanInputFiles();
        },
        onInputFiles(event) {
            const files = event.target.files;
            this.saveAttachments(files);
            this.cleanInputFiles();
        },
        cleanInputFiles() {
            try {
                this.$refs.fileInput.value = '';
                if (this.$refs.fileInput.value) {
                    this.$refs.fileInput.type = 'text';
                    this.$refs.fileInput.type = 'file';
                }
            } catch (e) {
                console.error(e);
            }
        },
        cleanInputFields() {
            this.form = {
                subject: '',
                description: '',
                email: '',
                attachments: [],
                question: {}
            };
        },
        showLoadingMessage() {
            this.showMessage({
                text: this.$t('message.loading'),
                type: MESSAGE_TYPE.WAITING
            });
        },
        async saveAttachment(file) {
            return new Promise((resolve) => {
                const formData = new FormData();
                for (const key in this.queryParams) {
                    formData.append(key, this.queryParams[key]);
                }
                formData.append('file', file);

                this.showLoadingMessage()

                const queryParam = {
                    _: Date.now(),
                };

                const uploadErrorType = this.isUploadErrorAlreadyWas ? 'uploadErrorSecondTime' : 'uploadErrorFirstTime';
                const fallbackErrorText = this.$t(`errors.${uploadErrorType}`);

                api.uploadTicket(formData, queryParam, fallbackErrorText)
                    .then(response => {
                        this.hideMessage();
                        this.isUploadErrorAlreadyWas = false;
                        resolve(response?.data?.data);
                    })
                    .catch(err => {
                        const isErrorDescriptionEmpty = !err.response?.data?.errors.length;
                        if (isErrorDescriptionEmpty) this.isUploadErrorAlreadyWas = true;
                        console.error(err);
                    });
            });
        },
        async saveAttachments(files) {
            const limitSize = 1024 * 1024 * this.maxFileSize;

            if (this.form.attachments.length + files.length > this.maxFilesCount) {
                const alertText = this.$t('errors.tooManyFiles', { max_files_count: this.maxFilesCount })
                this.showMessage({
                    text: alertText,
                    type: MESSAGE_TYPE.ERROR
                });
                return false;
            }

            for (let i = 0; i < files.length; i++) {
                const fileSize = files[i].size
                if (fileSize > limitSize) {
                    const alertTextRaw = this.$t('errors.attachmentSize', { max_file_size_in_mb: this.maxFileSize })
                    const alertText = messagePostProcess(alertTextRaw)
                    this.showMessage({
                        text: alertText,
                        type: MESSAGE_TYPE.ERROR
                    });
                    return false;
                }
            }

            for (let file of files) {
                const data = await this.saveAttachment(file);
                if (data?.token) {
                    this.form.attachments.push({
                        token: data.token,
                        name: file.name || data.fileName,
                        size: file.size,
                    });
                }
            }
        },
        async removeAttachment(selectedToken) {
            this.showLoadingMessage();
            try {
                const queryData = {
                    ...this.queryParams,
                    file_token: selectedToken,
                };
                await api.deleteUploadedImage(queryData);

                this.form.attachments = this.form.attachments.filter((attachment) => attachment.token !== selectedToken);
            } finally {
                this.hideMessage();
            }
        },
        validateFields() {
            const getErrorText = () => {
                // is tag selected
                if (!this.questionId) return 'errors.type';

                // timeout between messages
                if (this.waitTimeout > Date.now()) return 'errors.wait';

                // description not less than 10 symbols
                if (this.form.description.length < this.descriptionMinLength) return 'errors.description';

                // W3C HTML Standard
                const emailRegex = new RegExp(EMAIL_REG_EX);
                if (!emailRegex.test(this.form.email)) return 'errors.email';

                // Email field length not more than 255 characters
                if (this.form.email.length > EMAIL_FIELD_MAX_LENGTH) return 'errors.emailMaxLength';

                return '';
            }

            const errorText = getErrorText();
            if (errorText) {
                this.showMessage({
                    text: this.$t(errorText),
                    type: MESSAGE_TYPE.ERROR
                });
            }
            return !errorText;
        },
        serializeForm() {
            return {
                ...this.queryParams,
                question_id: this.questionId,
                subject: this.form.subject,
                description: this.form.description,
                email: this.form.email,
                attachments: this.form.attachments.map(attachment => attachment.token),
                browser: window.navigator.userAgent,
                localtime: (new Date()).toString(),
                network_test: this.networkTestData,
            };
        },
        async sendTicket() {
            if (!this.validateFields()) return;

            const fields = this.serializeForm();

            this.showLoadingMessage();
            try {
                await api.createTicket(fields);
                this.waitTimeout = Date.now() + this.sendTimeout;

                this.cleanInputFields();

                this.showMessage({
                    text: this.$t('message.sent'),
                    type: MESSAGE_TYPE.SUCCESS
                });
                this.setCachedFormData({})
            } catch (err) {
                console.error(err);
            }
        },
    }
}
</script>